/**
 * Rendering a server refusal. OWNER: web-frontend-engineer.
 *
 * `[T-73]`: THE HUMAN COPY RENDERS, THE MACHINE CODE IS LOGGED AND NEVER SHOWN.
 *
 * The server resolves its own human copy from `error.*` in the one catalogue,
 * so `body.message` is normally the right string and this app renders it as
 * given. Three refusals reach the browser WITHOUT server-authored copy, and
 * they are the reason this module exists:
 *
 *   · `NOT_AUTHENTICATED`  — the session ended under a request in flight.
 *   · `FORBIDDEN_FOR_ROLE` — a role guard refused before any handler ran.
 *   · `MALFORMED_REQUEST`  — `@pt/api-client`'s own last-resort body when a
 *     response carried no JSON at all, and the server's 500 handler.
 *
 * For those the catalogue key is resolved here. The rule is narrow and it holds
 * in one direction only: the catalogue is preferred ONLY where the server sent
 * no copy, so a server that speaks is never overruled by a client that guesses.
 */

import type { ApiRejection } from '@pt/api-client';
import { hasCopy } from '@pt/copy';
import { slotFreeLabel } from './label.ts';

/** The last-resort body `@pt/api-client` synthesises when there is no JSON. */
const CLIENT_SYNTHESISED = 'The atelier refused that.';

export function refusalCopy(rejection: ApiRejection): string {
  const body = rejection.body;
  const server = typeof body.message === 'string' ? body.message.trim() : '';

  // The server spoke. Render exactly what it said.
  if (server !== '' && server !== CLIENT_SYNTHESISED) return server;

  // It did not. Take the catalogue's sentence for this code, if it has one that
  // needs no slot we cannot fill.
  const byCode = slotFreeLabel(`error.${body.code}`);
  if (byCode !== null) return byCode;

  // The sentence needs slots we cannot fill (e.g. CONSTRAINT_VIOLATION wants
  // {optionLabel} and {reason}). Backend normally sends a filled `message` for
  // exactly this code; where it did not, the authored slotless twin is the
  // honest line — never an unfilled slot on a screen (clarity Finding 1).
  const generic = slotFreeLabel(`error.${body.code}.generic`);
  if (generic !== null) return generic;

  // A code the catalogue does not carry is still not an excuse to author a
  // sentence here: the generic shape-failure statement is the honest floor.
  const floor = slotFreeLabel('error.MALFORMED_REQUEST');
  return floor ?? '';
}

/** True where the catalogue can speak for this code without the server. */
export function catalogueSpeaksFor(code: string): boolean {
  return hasCopy(`error.${code}`);
}
